import React, { useState, useEffect } from 'react';
import {
    Table,
    Card,
    Typography,
    message,
    Space,
    Button,
    Modal,
    Form,
    Input,
    Popconfirm,
    InputNumber,
} from 'antd';
import {
    SortAscendingOutlined,
    ReloadOutlined,
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
} from '@ant-design/icons';
import { ApiService } from '../services/api';
import { SortCategory } from '../types';

const { Title } = Typography;

const SortCategoryManagement: React.FC = () => {
    const [sortCategories, setSortCategories] = useState<SortCategory[]>([]);
    const [loading, setLoading] = useState(false);
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editingCategory, setEditingCategory] = useState<SortCategory | null>(null);
    const [createForm] = Form.useForm();
    const [editForm] = Form.useForm();

    useEffect(() => {
        loadSortCategories();
    }, []);

    const loadSortCategories = async () => {
        setLoading(true);
        try {
            console.log('开始加载排序类别列表...');
            const response = await ApiService.getSortCategoryList({
                page: 1,
                page_size: 100 // 获取所有数据用于排序
            });
            console.log('API响应:', response);

            if (response.code === 200) {
                // 按sort_index排序
                const sortedCategories = response.data.sort_categories.sort((a, b) => a.sort_index - b.sort_index);
                setSortCategories(sortedCategories);
                console.log('排序类别列表加载成功:', sortedCategories);
            } else {
                message.error(response.message || '获取排序类别列表失败');
            }
        } catch (error: any) {
            console.error('加载排序类别列表失败:', error);
            message.error(error.response?.data?.message || '获取排序类别列表失败');
        } finally {
            setLoading(false);
        }
    };

    // 表格列定义
    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 80,
        },
        {
            title: '排序索引',
            dataIndex: 'sort_index',
            key: 'sort_index',
            width: 120,
            sorter: (a: SortCategory, b: SortCategory) => a.sort_index - b.sort_index,
        },
        {
            title: '备注说明',
            dataIndex: 'remark',
            key: 'remark',
        },
        {
            title: '操作',
            key: 'action',
            width: 200,
            render: (_: any, record: SortCategory) => (
                <Space size="middle">
                    <Button
                        type="link"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            console.log('点击编辑按钮，记录:', record);
                            handleEdit(record);
                        }}
                    >
                        编辑
                    </Button>
                    <Popconfirm
                        title="确定要删除这个排序类别吗？"
                        onConfirm={(e) => {
                            e?.preventDefault();
                            e?.stopPropagation();
                            handleDelete(record.id);
                        }}
                        okText="确定"
                        cancelText="取消"
                    >
                        <Button
                            type="link"
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                            }}
                        >
                            删除
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    // 创建排序类别
    const handleCreate = async (values: any) => {
        try {
            console.log('创建排序类别:', values);
            const response = await ApiService.createSortCategory(values);
            console.log('创建响应:', response);

            if (response.code === 200) {
                message.success('排序类别创建成功');
                setCreateModalOpen(false);
                createForm.resetFields();
                loadSortCategories();
            } else {
                message.error(response.message || '创建失败');
            }
        } catch (error: any) {
            console.error('创建排序类别失败:', error);
            message.error(error.response?.data?.message || '创建失败');
        }
    };

    // 编辑排序类别
    const handleEdit = (record: SortCategory) => {
        console.log('编辑排序类别:', record);
        try {
            setEditingCategory(record);
            editForm.setFieldsValue({
                sort_index: record.sort_index,
                remark: record.remark
            });
            setEditModalOpen(true);
            console.log('编辑模态框已打开');
        } catch (error) {
            console.error('编辑处理错误:', error);
            message.error('打开编辑窗口失败');
        }
    };

    // 更新排序类别
    const handleUpdate = async (values: any) => {
        if (!editingCategory) {
            console.error('没有选择要编辑的类别');
            return;
        }

        try {
            console.log('更新排序类别:', editingCategory.id, values);
            const response = await ApiService.updateSortCategory({
                id: editingCategory.id,
                ...values
            });
            console.log('更新响应:', response);

            if (response.code === 200) {
                message.success('排序类别更新成功');
                setEditModalOpen(false);
                setEditingCategory(null);
                editForm.resetFields();
                loadSortCategories();
            } else {
                message.error(response.message || '更新失败');
            }
        } catch (error: any) {
            console.error('更新排序类别失败:', error);
            message.error(error.response?.data?.message || '更新失败');
        }
    };

    // 删除排序类别
    const handleDelete = async (id: number) => {
        try {
            console.log('删除排序类别:', id);
            const response = await ApiService.deleteSortCategory({ id });
            console.log('删除响应:', response);

            if (response.code === 200) {
                message.success('排序类别删除成功');
                loadSortCategories();
            } else {
                message.error(response.message || '删除失败');
            }
        } catch (error: any) {
            console.error('删除排序类别失败:', error);
            message.error(error.response?.data?.message || '删除失败');
        }
    };

    return (
        <div style={{ padding: '24px' }}>
            <Card>
                <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Title level={3} style={{ margin: 0 }}>
                        <SortAscendingOutlined style={{ marginRight: '8px' }} />
                        排序类别管理
                    </Title>
                    <Space>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => {
                                console.log('打开创建模态框');
                                setCreateModalOpen(true);
                            }}
                        >
                            新增排序类别
                        </Button>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={() => {
                                console.log('刷新数据');
                                loadSortCategories();
                            }}
                        >
                            刷新
                        </Button>
                    </Space>
                </div>

                <Table
                    columns={columns}
                    dataSource={sortCategories}
                    rowKey={(record) => record.id.toString()}
                    loading={loading}
                    pagination={{
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条/共 ${total} 条`,
                    }}
                    size="middle"
                />
            </Card>

            {/* 创建模态框 */}
            <Modal
                title="新增排序类别"
                open={createModalOpen}
                onCancel={() => {
                    console.log('取消创建');
                    setCreateModalOpen(false);
                    createForm.resetFields();
                }}
                onOk={() => {
                    console.log('确认创建');
                    createForm.submit();
                }}
                okText="确定"
                cancelText="取消"
            >
                <Form
                    form={createForm}
                    layout="vertical"
                    onFinish={handleCreate}
                >
                    <Form.Item
                        label="排序索引"
                        name="sort_index"
                        rules={[
                            { required: true, message: '请输入排序索引' },
                            { type: 'number', min: 1, message: '排序索引必须大于0' }
                        ]}
                        extra="数字越小，排序越靠前"
                    >
                        <InputNumber
                            placeholder="请输入排序索引"
                            style={{ width: '100%' }}
                            min={1}
                        />
                    </Form.Item>
                    <Form.Item
                        label="备注说明"
                        name="remark"
                        rules={[
                            { required: true, message: '请输入备注说明' },
                            { max: 255, message: '备注说明最多255个字符' }
                        ]}
                    >
                        <Input.TextArea
                            placeholder="请输入备注说明"
                            rows={3}
                        />
                    </Form.Item>
                </Form>
            </Modal>

            {/* 编辑模态框 */}
            <Modal
                title="编辑排序类别"
                open={editModalOpen}
                onCancel={() => {
                    console.log('取消编辑');
                    setEditModalOpen(false);
                    setEditingCategory(null);
                    editForm.resetFields();
                }}
                onOk={() => {
                    console.log('确认编辑');
                    editForm.submit();
                }}
                okText="确定"
                cancelText="取消"
            >
                <Form
                    form={editForm}
                    layout="vertical"
                    onFinish={handleUpdate}
                >
                    <Form.Item
                        label="排序索引"
                        name="sort_index"
                        rules={[
                            { required: true, message: '请输入排序索引' },
                            { type: 'number', min: 1, message: '排序索引必须大于0' }
                        ]}
                        extra="数字越小，排序越靠前"
                    >
                        <InputNumber
                            placeholder="请输入排序索引"
                            style={{ width: '100%' }}
                            min={1}
                        />
                    </Form.Item>
                    <Form.Item
                        label="备注说明"
                        name="remark"
                        rules={[
                            { required: true, message: '请输入备注说明' },
                            { max: 255, message: '备注说明最多255个字符' }
                        ]}
                    >
                        <Input.TextArea
                            placeholder="请输入备注说明"
                            rows={3}
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default SortCategoryManagement; 